/**
 * Foundation/Core — NotificationBell
 *
 * Campanella notifiche generica della Gruppo Impresa Suite: rendering, fetch,
 * mark-read/delete, tabs, integrazione DetailDialog. Nessuna conoscenza di kind,
 * categorie o dominio Product: tutto passa dal chiamante tramite prop.
 *
 * PROP
 * - `resolveNotificationMeta(n) => meta` (obbligatoria):
 *     dispatcher Product-specific che risolve una notifica server-side in un
 *     `meta` conforme al contratto di `NotificationDetailDialog`.
 * - `tabs` (opzionale, default [unread, recent]):
 *     configurazione dei tab, array di `{ key, label }`.
 * - `onNotify(n, meta)` (opzionale):
 *     callback invocato quando una nuova notifica arriva (per fire desktop
 *     notification, toast, ecc. — logica di gating delegata al Product Pack).
 * - `isEnabled` (opzionale, default true):
 *     flag di visibilita' — il Product Pack calcola le proprie regole RBAC.
 * - `pollingIntervalMs` (opzionale, default 30_000).
 *
 * ENDPOINT BACKEND FOUNDATION:
 *   GET    /api/notifications
 *   POST   /api/notifications/mark-read
 *   DELETE /api/notifications/:id
 *   DELETE /api/notifications/all
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/foundation/api/client";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtRelative } from "./formatters";
import { NotificationDetailDialog } from "./NotificationDetailDialog";

const DEFAULT_TABS = [
  { key: "unread", label: "Non lette" },
  { key: "recent", label: "Recenti" },
];

export function NotificationBell({
  resolveNotificationMeta,
  tabs = DEFAULT_TABS,
  onNotify,
  isEnabled = true,
  pollingIntervalMs = 30_000,
}) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(tabs[0]?.key || "unread");
  const [detailId, setDetailId] = useState(null);
  const lastSeenIds = useRef(new Set());
  const firstFetch = useRef(true);

  const fetchNotifs = async () => {
    if (!isEnabled) return;
    try {
      const { data } = await api.get("/notifications", { params: { unread_only: false, limit: 30 } });
      const newItems = data.items || [];
      if (!firstFetch.current) {
        for (const n of newItems) {
          if (!lastSeenIds.current.has(n.id) && !n.is_read) {
            const meta = resolveNotificationMeta ? resolveNotificationMeta(n) : null;
            const title = meta?.title || "Nuova notifica";
            const body = meta?.body || "";
            toast(title, { description: body });
            if (onNotify) {
              try { onNotify(n, meta, { openDetail: () => setDetailId(n.id) }); }
              catch (_) { /* silenzioso */ }
            }
          }
        }
      }
      firstFetch.current = false;
      lastSeenIds.current = new Set(newItems.map((n) => n.id));
      setItems(newItems);
      setUnread(newItems.filter((n) => !n.is_read).length);
    } catch (_) { /* silenzioso: notifiche non critiche */ }
  };

  useEffect(() => {
    if (!isEnabled) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, pollingIntervalMs);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [isEnabled, pollingIntervalMs]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { ids: null });
      fetchNotifs();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const markOne = async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnread((prev) => Math.max(0, prev - 1));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deleteOne = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      fetchNotifs();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const clearRead = async () => {
    try {
      await api.delete("/notifications/all");
      fetchNotifs();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDetail = (n) => {
    setDetailId(n.id);
    if (!n.is_read) markOne(n.id);
  };

  const visibleItems = useMemo(() => {
    if (tab === "unread") return items.filter((n) => !n.is_read);
    return items;
  }, [items, tab]);

  const detailMeta = useMemo(() => {
    if (!detailId) return null;
    const n = items.find((x) => x.id === detailId);
    return n && resolveNotificationMeta ? resolveNotificationMeta(n) : null;
  }, [detailId, items, resolveNotificationMeta]);

  if (!isEnabled) return null;

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-slate-600 hover:text-emerald-800 hover:bg-emerald-50"
          data-testid="notification-bell-btn"
          aria-label="Notifiche"
        >
          <Bell size={18} strokeWidth={1.75} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center
                         rounded-full bg-red-600 text-white text-[10px] font-bold border-2 border-white"
              data-testid="notification-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[380px] p-0 max-h-[75vh] overflow-hidden flex flex-col"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-emerald-50/50">
          <div>
            <div className="text-sm font-semibold text-slate-900">Notifiche</div>
            <div className="text-[11px] text-slate-500">
              {unread > 0 ? `${unread} non ${unread === 1 ? "letta" : "lette"}` : "Tutte lette"}
            </div>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead}
                    className="text-emerald-700 hover:bg-emerald-100 h-8"
                    data-testid="notification-mark-all-read">
              <Check size={14} className="mr-1" /> Segna tutte
            </Button>
          )}
        </div>

        <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-100 bg-white" data-testid="notification-tabs">
          {tabs.map((t) => {
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`notification-tab-${t.key}`}
                className={`text-[11px] uppercase tracking-[0.14em] font-semibold px-2.5 py-1 rounded-md transition-colors ${
                  isActive
                    ? "bg-suite-primary text-white"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {t.label}
                {t.key === "unread" && unread > 0 && (
                  <span className={`ml-1.5 text-[10px] font-bold ${isActive ? "text-white" : "text-red-600"}`}>
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {visibleItems.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-10 px-4" data-testid="notification-empty">
              {tab === "unread" ? "Nessuna notifica da leggere." : "Nessuna notifica al momento."}
            </div>
          ) : (
            visibleItems.map((n) => {
              const meta = resolveNotificationMeta ? resolveNotificationMeta(n) : null;
              if (!meta) return null;
              const sev = meta.severity;
              const dotColor = !n.is_read ? sev.dot : "bg-slate-300";
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openDetail(n)}
                  data-testid={`notification-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 hover:bg-suite-primary-light/40 transition-colors flex items-start gap-2.5 ${
                    n.is_read ? "opacity-60" : "bg-white"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotColor}`} aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-[9px] uppercase tracking-[0.14em] font-bold ${
                        sev.key === "critical" ? "text-red-700"
                        : sev.key === "warning" ? "text-amber-700"
                        : "text-emerald-700"
                      }`} data-testid={`notification-severity-${n.id}`}>
                        {sev.label}
                      </span>
                      <span className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-400">
                        · {meta.module}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-slate-900 leading-tight">{meta.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{meta.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                      {fmtRelative(n.created_at)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {items.some((n) => n.is_read) && (
          <div className="border-t border-slate-200 px-3 py-2">
            <Button size="sm" variant="ghost" onClick={clearRead}
                    className="text-slate-500 hover:text-red-700 hover:bg-red-50 h-8 w-full justify-center"
                    data-testid="notification-clear-read">
              <Trash2 size={13} className="mr-1" /> Elimina le lette
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>

    <NotificationDetailDialog
      meta={detailMeta}
      open={!!detailId}
      onClose={() => setDetailId(null)}
      onDelete={deleteOne}
    />
    </>
  );
}

export default NotificationBell;
